// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/guestApi.ts
// 🔧 PURPOSE: API functions for guest authentication and chat
// ============================================

import i18n, { tr } from '../i18n';
import { appLanguageOf, manualLanguage } from './locale';
import type { WalletCardView } from './giftCardWallet';

const CHANNEL_MANAGER_URL = 'https://book.nonnavittoriaapartments.it';

/**
 * Le foto del catalogo (`imageUrl`) arrivano dal channel manager e possono
 * essere RELATIVE ("/voice-os/caffe-brioche.svg"): sul tablet quel percorso
 * sta sullo stesso dominio, in app no. Senza questa risoluzione il browser
 * andrebbe a cercare l'immagine dentro l'app e troverebbe un 404 — cioe' un
 * riquadro vuoto al posto della foto grande della schermata dedicata.
 */
export const guestAssetUrl = (url?: string | null): string | null => {
  const raw = String(url || '').trim();
  if (!raw) return null;
  if (/^(https?:)?\/\//.test(raw) || raw.indexOf('data:') === 0) return raw;
  return `${CHANNEL_MANAGER_URL}${raw.charAt(0) === '/' ? '' : '/'}${raw}`;
};

// Stato normalizzato del volo (deciso lato channel manager, vedi
// src/lib/flight-public.ts). L'app non interpreta mai le stringhe grezze dei
// provider: si basa solo su questo enum.
export type FlightState =
  | 'scheduled'
  | 'on-time'
  | 'delayed'
  | 'boarding'
  | 'in-air'
  | 'landed'
  | 'cancelled'
  | 'diverted'
  | 'unknown';

export interface FlightStatus {
  number: string;
  airline?: string;
  role: 'arrival' | 'departure';
  verified: boolean;
  depIata?: string;
  depName?: string;
  arrIata?: string;
  arrName?: string;
  scheduledDeparture?: string;
  scheduledArrival?: string;
  estimatedArrival?: string;
  /** Giorno del volo "YYYY-MM-DD" (channel manager nuovo; assente coi vecchi). */
  date?: string;
  delayMinutes: number;
  state: FlightState;
  liveStatus?: string;
  lastCheckedAt?: string;
  // Solo per role 'departure': orario consigliato di partenza dall'alloggio
  // (HH:MM) = orario volo − 2h. Le partenze non sono tracciate live.
  recommendedLeaveTime?: string;
}

export interface FlightStatusResponse {
  success: boolean;
  hasFlight: boolean;
  flight?: FlightStatus;      // volo d'arrivo (tracciato live)
  departure?: FlightStatus;   // volo di partenza (statico, niente tracking)
  error?: string;
  errorCode?: string;
}

/** Motivo per cui il check-in va rifatto (lo sceglie il gestore). */
export type CheckInRedoReason = 'wrong_data' | 'missing_document' | 'missing_guests' | 'other';

export interface CheckInRedo {
  reason: CheckInRedoReason;
  /** La nota del gestore, cosi' come l'ha scritta. */
  note: string | null;
  requestedAt: string;
}

export interface GuestBooking {
  id: string;
  bookingReference: string;
  guestName: string;
  guestEmail: string;
  apartmentName: string;
  apartmentAddress?: string;
  apartmentLatitude?: number | null;
  apartmentLongitude?: number | null;
  /**
   * La foto principale dell'appartamento (l'originale https sul channel
   * manager), per l'intestazione della Home: la carica lib/apartmentPhoto,
   * ridimensionata. Arriva solo da booking/info (non dal login: c'è dal
   * primo aggiornamento della sessione); per un gruppo è quella
   * dell'appartamento della prenotazione con cui si è entrati. null o
   * assente (channel manager vecchio) = intestazione marrone di sempre.
   */
  apartmentPhotoUrl?: string | null;
  checkIn: string;
  checkOut: string;
  numberOfGuests: number;
  status: string;
  /** La lingua scelta al check-in online ("de"): l'app si imposta così al login. */
  language?: string | null;
  onlineCheckInCompleted?: boolean;
  onlineCheckInCompletedAt?: string | null;
  /**
   * Il gestore ha rifiutato il check-in online e chiede di rifarlo (dati
   * diversi dai documenti, documento mancante...): motivo e nota. Arriva
   * da booking/info; null o assente = niente da rifare (o channel manager
   * vecchio).
   */
  checkInRedo?: CheckInRedo | null;
  expectedArrivalTime?: string | null;
  /**
   * L'orario d'arrivo DICHIARATO dall'ospite nel check-in online ("HH:mm"
   * di Roma), null se non l'ha mai detto. `expectedArrivalTime` ricade
   * sulla data del check-in e non distingue i due casi. Assente sui
   * channel manager vecchi. Serve alla proposta del check-in anticipato.
   */
  declaredArrivalTime?: string | null;
  source?: string;
  // Group booking fields
  isGroupBooking?: boolean;
  groupBookingId?: string;
  // Rate plan policy: true = non-refundable, false = refundable, undefined = OTA/legacy (no badge)
  isNonRefundable?: boolean;
  // Snapshot del volo d'arrivo agganciato al transfer aeroporto (può essere
  // leggermente stale). null/undefined quando non c'è un transfer con volo.
  // Lo stato live aggiornato si ottiene con fetchFlightStatus().
  flight?: FlightStatus | null;
  // Snapshot del volo di partenza (transfer al checkout): statico.
  departure?: FlightStatus | null;
  // Imposta di soggiorno chiesta dalla struttura (riscossione ibrida).
  // null/undefined = mai richiesta o riscossa dal portale: nessuna card.
  touristTax?: TouristTaxInfo | null;
  // Ricevute già emesse (soggiorno, extra, quietanza imposta) con link download,
  // e stato della richiesta "ricevuta del soggiorno" dall'app.
  receipts?: ReceiptInfo[];
  receiptRequest?: ReceiptRequestState | null;
  // Orario di uscita VERO: "11:00" quando l'ospite ha comprato il check-out
  // posticipato, altrimenti null (vale quello standard della struttura).
  customCheckOutTime?: string | null;
  // Gli orari gia' decisi dal channel manager per questa prenotazione: le
  // Impostazioni della struttura, piu' il late check-out o il check-in
  // anticipato comprati. Assenti sui channel manager vecchi: l'app ricade
  // su customCheckOutTime e poi su 15:00 / 10:00 (lib/houseTimes).
  checkInTime?: string | null;
  checkOutTime?: string | null;
  // Cosa è già stato acquistato per questo soggiorno. Senza questo elenco
  // l'app riproponeva servizi già pagati.
  purchasedExtras?: PurchasedExtra[];
}

// ============================================
// EXTRA & SERVIZI (sezione acquisti in app)
// ============================================

/** Un extra già pagato per questa prenotazione. */
export interface PurchasedExtra {
  serviceId: string;
  serviceName: string;
  quantity: number;
  total: number;
  /** Orario scelto, per gli extra "a orario" (check-in anticipato, uscita posticipata). */
  selectedTime?: string | null;
  /** Riepilogo leggibile costruito dal server (varianti, date, note). */
  summary?: string | null;
  /** Orario di consegna comunicato dallo staff (ISO), per caffe' & brioche, vino, forniture. */
  deliveryAt?: string | null;
  deliveryStatus?: 'scheduled' | 'delivered' | 'cancelled' | null;
  /**
   * Quali articoli, bottiglie o confezioni della riga (channel manager
   * nuovo): le combinazioni smart non ripropongono il dentifricio a chi l'ha
   * già comprato. Assente = si sa solo il servizio.
   */
  variantIds?: string[];
}

/** A che punto del soggiorno siamo, deciso dal server (giorni di Roma). */
export type StayPhase = 'before_checkin' | 'in_stay' | 'checkout_day' | 'after_checkout';

/**
 * Perché un extra non è acquistabile adesso. Codici stabili decisi dal
 * channel manager: l'app ci attacca sopra il testo tradotto e non li
 * interpreta mai come frasi.
 */
export type ExtraUnavailableReason =
  | 'wrong_phase'
  | 'outside_window'
  | 'not_enough_nights'
  | 'too_late'
  | 'already_purchased'
  | 'after_cutoff'
  | 'sold_out'
  | 'no_cleaning_dates'
  | 'on_request'
  // Il giorno della partenza, dall'orario di check-out più un'ora: niente più
  // consegne o servizi in casa (taxi e transfer restano).
  | 'stay_over'
  // Check-in anticipato: nessuna fascia si può più comprare adesso
  // (passate, a meno di 30 minuti, spente o prima che l'appartamento si liberi).
  | 'no_time_slots';

/**
 * Listino e termini del TRANSFER (taxi), così come li manda il channel
 * manager. Servono all'app per costruire il form e per mostrare
 * un'anteprima di prezzo che coincida con l'addebito (src/lib/transferPricing.ts).
 */
export interface TransferOptions {
  hasRoundTrip: boolean;
  arrivalPrice: number;
  departurePrice: number;
  stationOptions: Array<{ name: string; price: number }>;
  requiresFlightInfo: boolean;
  maxPersonsPerTransfer: number;
  /** Veicoli necessari per gli ospiti di questa prenotazione (informativo). */
  vehicles: number;
  nightSurchargeEnabled: boolean;
  nightSurchargeStart: string;
  nightSurchargeEnd: string;
  nightSurchargeAmount: number;
  /** Giorni di anticipo richiesti per comprare online (0 = nessun termine). */
  minAdvanceDays: number;
  /**
   * Se la tratta di ARRIVO si può ancora scegliere in questa fase del
   * soggiorno: chi è già in casa non ha più un arrivo da farsi portare.
   * È un'altra cosa da `arrivalOpen`, che parla solo del termine di anticipo
   * — e il server rifiuta comunque `isArrival` con `arrival_not_available`.
   */
  arrivalAllowed?: boolean;
  /** Termine per la tratta di ANDATA (ISO) e se è ancora aperto. */
  arrivalDeadline: string | null;
  arrivalOpen: boolean;
  /** Idem per il RITORNO: si misura sulla partenza, non sull'arrivo. */
  departureDeadline: string | null;
  departureOpen: boolean;
}

/**
 * Un ARTICOLO di un servizio a scelta singola (carta igienica, saponi, kit,
 * biancheria). Il servizio non ha un prezzo suo: si sceglie un articolo e
 * quante unita', e il totale e' prezzo dell'articolo per la quantita'.
 */
export interface ItemVariant {
  id: string;
  name: string;
  description?: string;
  /**
   * Il testo dei dettagli (i kit: cosa si compra e come arriva, in 8 lingue
   * dal channel manager). Aprendo l'articolo si legge questo al posto della
   * foto grande; la riga corta sotto il nome resta `description`. Assente sui
   * channel manager vecchi e sugli articoli senza testo: resta la foto.
   */
  detailText?: string;
  price: number;
  imageUrl?: string;
  /**
   * Prezzo "pieno" da mostrare barrato (somma dei pezzi singoli). Il server
   * lo manda solo quando è maggiore di `price`: l'app lo riverifica comunque.
   */
  compareAtPrice?: number;
  /** 'popular' = l'articolo più richiesto: badge e primo della lista. */
  highlight?: 'popular';
  /**
   * Sconto dell'articolo (es. 10 = −10%), come quello delle bottiglie: il
   * prezzo pagato è lo scontato. Assente sui channel manager vecchi.
   */
  discountPercentage?: number;
  /** Magazzino: pezzi ancora disponibili (solo se il gestore li conta). */
  stockLeft?: number;
  /** Magazzino: contato e finito ("Esaurito"). */
  soldOut?: boolean;
  /**
   * Il Cestino della Nonna: la bottiglia la sceglie l'ospite (rosso, bianco,
   * prosecco, spritz), fra quelle in vendita adesso. Nomi generici, già
   * tradotti. Assente sui channel manager vecchi e sugli altri articoli.
   */
  drinkChoice?: KitDrinkChoice;
}

/** Le bottiglie fra cui scegliere quella di un kit. */
export type KitDrinkKind = 'red' | 'white' | 'prosecco' | 'spritz';
export interface KitDrinkChoice {
  required: boolean;
  /** L'id della bottiglia usata se non se ne sceglie una. */
  default: string;
  options: Array<{ id: string; kind: KitDrinkKind; name: string }>;
}

/** Una confezione di "Caffè & Brioche" (prezzo del bundle scelto). */
export interface BundleVariant {
  id: string;
  coffeeQty: number;
  briocheQty: number;
  price: number;
  imageUrl?: string;
  description?: string;
  /** Prezzo dei pezzi presi singolarmente, da barrare (solo se > price). */
  compareAtPrice?: number;
  /** Magazzino: confezioni ancora disponibili (solo se il gestore le conta). */
  stockLeft?: number;
  /** Magazzino: contata e finita ("Esaurito"). */
  soldOut?: boolean;
}

/**
 * La scheda di una bottiglia di "Vino & Prosecco", come la manda il channel
 * manager: tutti i campi sono facoltativi e già nella lingua dell'app.
 * `heroImageUrl` può essere relativo, come `imageUrl`: passa da guestAssetUrl.
 */
export interface WineInfo {
  producer?: string;
  appellation?: string;
  grape?: string;
  vintage?: string;
  /** Gradazione in % vol (es. 14.5). */
  abv?: number;
  aging?: string;
  sizeMl?: number;
  servingTemp?: string;
  /** Pillola breve, es. "Tipico della Puglia". */
  badge?: string;
  tagline?: string;
  story?: string;
  notes?: string[];
  pairings?: string[];
  heroImageUrl?: string;
  // --- La scheda dello SPRITZ e del PROSECCO (campi aggiunti dal channel manager) ---
  /**
   * 'spritz' = scheda arancio, "Scopri lo spritz"; 'prosecco' = scheda oro,
   * "Scopri il prosecco"; 'white' = scheda verde-paglierino, "Scopri il vino
   * bianco". Assente (o un tipo che l'app non conosce) = vino.
   */
  kind?: 'spritz' | 'prosecco' | 'white';
  /** Gradazione come intervallo (es. 8–11 % vol): arrivano sempre insieme. */
  abvMin?: number;
  abvMax?: number;
  /** Circa quanti bicchieri fa la bottiglia, e circa quanti ml ciascuno. */
  servings?: number;
  servingMl?: number;
  /** "La marca può variare secondo disponibilità". */
  brandMayVary?: boolean;
  /** Avviso 18+ ("Bevanda alcolica, solo per maggiorenni"). */
  adultsOnly?: boolean;
  /** "Contiene solfiti", nello stesso avviso. */
  containsSulphites?: boolean;
  /** "Come servirlo": i passi, già tradotti. */
  serveSteps?: string[];
  /** La riga sotto i passi (es. come conservarla una volta aperta). */
  serveNote?: string;
  // --- La scheda del PROSECCO (campi aggiunti dal channel manager) ---
  /**
   * Il nome grande della scheda ("Prosecco"), quando è diverso dal nome della
   * bottiglia ("Bottiglia di Prosecco"). Assente = il nome della bottiglia.
   * Solo la scheda: righe e carrello restano col nome della bottiglia.
   */
  title?: string;
  /** La riga sotto i profumi ("I profumi tipici del prosecco…"). */
  tastingNote?: string;
}

export interface AppExtraOptions {
  requiresTimeSelection?: boolean;
  /**
   * Le fasce orarie. Per il check-in anticipato il channel manager le manda
   * già valutate adesso: `isAvailable: false` anche per quelle passate, a
   * meno di 30 minuti o prima che l'appartamento si liberi, col motivo in
   * `unavailableReason` (assente sui channel manager vecchi).
   */
  timeSlotOptions?: Array<{
    time: string;
    price: number;
    isAvailable: boolean;
    unavailableReason?: 'unavailable' | 'past' | 'too_close' | 'apartment_busy' | 'unknown_slot';
  }>;
  /** Foto e descrizione (già tradotta) della variante, quando il gestore le ha caricate. */
  cleaningVariants?: Array<{ id: string; name: string; price: number; imageUrl?: string; description?: string }>;
  /**
   * Le bottiglie in vendita per la "Bottiglia di benvenuto". Il servizio non
   * ha un prezzo suo: il totale è la somma delle bottiglie scelte, con lo
   * sconto per-variante — la stessa regola che il server riapplica al
   * pagamento, così il prezzo visto è il prezzo pagato.
   */
  wineVariants?: Array<{
    id: string;
    name: string;
    price: number;
    isAvailable: boolean;
    /** Sconto della singola bottiglia (es. 10 = −10%). */
    discountPercentage?: number;
    imageUrl?: string;
    description?: string;
    /** La bottiglia "di punta": prima della lista, con la card in evidenza. */
    featured?: boolean;
    /** La scheda del vino (facoltativa, già tradotta): apre "Scopri il vino" (o "Scopri lo spritz", "Scopri il prosecco"). */
    info?: WineInfo;
    /** Magazzino: bottiglie ancora disponibili (solo se il gestore le conta). */
    stockLeft?: number;
    /** Magazzino: contata e finita (`isAvailable` è già false): "Esaurita". */
    soldOut?: boolean;
    /** Bevanda alcolica (18+), dal channel manager nuovo. Assente = vale la famiglia (vino). */
    alcoholic?: boolean;
  }>;
  /**
   * Le confezioni di "Caffè & Brioche": il prezzo non è quello del servizio
   * ma quello del bundle scelto (es. 5 caffè + 5 brioche · 7 €). Stesse
   * opzioni del tablet, e il server riprezza comunque dall'id.
   */
  bundleVariants?: BundleVariant[];
  /**
   * Gli articoli in vendita per questo servizio, gia' tradotti, filtrati sui
   * disponibili e in ordine. Ci sono solo per i servizi "scegli un articolo":
   * quando ci sono, il prezzo del servizio (0) non conta piu' — vale quello
   * dell'articolo scelto, che il server riprezza comunque dall'id.
   */
  itemVariants?: ItemVariant[];
  /**
   * Servizio consegnato a mano (bottiglia, caffè & brioche): niente finestra
   * oraria — l'host l'ha tolta apposta — e al suo posto la stima di consegna,
   * la stessa che legge chi compra dal tablet.
   */
  deliveryEstimate?: boolean;
  allowMultipleQuantity?: boolean;
  requiresNotes?: boolean;
  /** Giorni ancora acquistabili per la pulizia intermedia ("YYYY-MM-DD"). */
  cleaningDates?: string[];
  /** Scorta fisica (culla): pezzi liberi / totali per queste notti. */
  inventoryAvailable?: number;
  inventoryTotal?: number;
  /** Solo per i due taxi: listino delle tratte e termini di anticipo. */
  transfer?: TransferOptions;
  /**
   * MAGAZZINO (channel manager nuovo): articoli e confezioni CONTATI e
   * finiti. Non stanno negli elenchi sopra (le app vecchie li venderebbero):
   * si mostrano spenti con "Esaurito".
   */
  soldOutItemVariants?: ItemVariant[];
  soldOutBundleVariants?: BundleVariant[];
  /** Magazzino, servizio senza varianti: quanti se ne possono ancora prendere. */
  stockLeft?: number;
  /** Magazzino: il servizio (o tutte le sue varianti) è finito. */
  soldOut?: boolean;
  /**
   * Check-in anticipato: chi parte dallo stesso appartamento esce tardi
   * (late check-out) e si entra solo da quest'ora ("14:00"). Assente se
   * non c'è nessun limite.
   */
  earlyCheckinMinTime?: string;
}

/** Una card della sezione "Extra & servizi". */
export interface AppExtra {
  serviceId: string;
  /** Già tradotto dal server nella lingua dell'ospite. */
  name: string;
  description: string;
  category: string;
  priceType: 'fixed' | 'per_person' | 'per_night' | 'per_person_night' | 'on_request';
  price: number;
  imageUrl?: string;
  available: boolean;
  reason?: ExtraUnavailableReason | null;
  /** Termine d'ordine superato (culla): ISO, per il messaggio "entro il ...". */
  deadline?: string | null;
  alreadyPurchased: boolean;
  /** true = non si compra, si CHIEDE (noleggi, culla oltre il termine). */
  canRequest: boolean;
  nightsRemaining: number;
  minAdvanceHours?: number;
  windowFrom?: string;
  windowTo?: string;
  /**
   * Un PRODOTTO consegnato in appartamento (caffè & brioche, tarallini,
   * bottiglie…): conta per l'ordine minimo del catalogo (`minProductOrder`).
   */
  deliveredProduct?: boolean;
  options: AppExtraOptions;
}

/** Stato dell'offerta "esci un'ora più tardi". */
export type LateCheckoutStatus = 'non_disponibile' | 'disponibile' | 'in_attesa';

export interface LateCheckoutOffer {
  status: LateCheckoutStatus;
  reason: string;
  price: number;
  /** Orario standard di uscita ("10:00"). */
  fromTime: string;
  /** Orario venduto ("11:00"). */
  toTime: string;
  /** Quando maturerà l'offerta, se in attesa (ISO). */
  availableFrom?: string | null;
  purchasedAt?: string | null;
}

/**
 * Un appartamento del gruppo per cui l'ospite può comprare un extra ("per
 * quale appartamento?"). Lo manda solo il channel manager nuovo, e solo per
 * le prenotazioni di gruppo; `own` è la prenotazione con cui si è entrati.
 */
export interface GroupApartment {
  bookingId: string;
  apartmentId: string;
  apartmentName: string;
  numberOfGuests: number;
  own: boolean;
}

export interface ExtrasCatalog {
  success: boolean;
  stayPhase?: StayPhase;
  customCheckOutTime?: string | null;
  /**
   * L'orario di uscita da cui il channel manager conta la fine del
   * soggiorno: quello della prenotazione, altrimenti quello della casa.
   * Assente sui channel manager vecchi, null = 10:00.
   */
  checkOutTime?: string | null;
  /** L'ora di check-in, come checkOutTime. Assente sui channel manager vecchi = 15:00. */
  checkInTime?: string | null;
  items: AppExtra[];
  purchasedExtras: PurchasedExtra[];
  pendingRequests: Array<{ serviceId: string; status: string; requestedAt?: string }>;
  lateCheckout?: LateCheckoutOffer | null;
  /**
   * L'ordine minimo dei prodotti consegnati in un acquisto, in euro (0 =
   * nessuno). Assente sui channel manager vecchi: nessun controllo in app,
   * decide il server.
   */
  minProductOrder?: number;
  /**
   * Gruppo: gli appartamenti per cui si può comprare. Assente sui channel
   * manager vecchi e per le prenotazioni singole: si compra per la propria.
   */
  groupApartments?: GroupApartment[];
  /**
   * Le COMBINAZIONI SMART (channel manager nuovo): "hai aggiunto X, ti serve
   * anche Y?". Vuote o assenti = nessuna proposta (spente dal gestore, o
   * channel manager vecchio). Vedi src/lib/upsell.ts.
   */
  upsell?: UpsellRule[];
  /**
   * Il pagamento accetta più righe dello stesso servizio con articoli
   * diversi (spazzolino e dentifricio). Assente = una riga per servizio.
   */
  multiItemLines?: boolean;
  error?: string;
  errorCode?: string;
}

/** Una regola delle combinazioni smart, come la manda il channel manager. */
export interface UpsellRule {
  id: string;
  /** "serviceId", "serviceId:variantId", "tag:alcohol" o "cart:min_order". */
  when: string;
  /** "serviceId", "serviceId:variantId" o "auto:min_order". */
  suggest: string;
  /** extras.upsell.reasons.<reasonKey> */
  reasonKey: string;
  /** Niente proposta se nel carrello o fra gli acquisti c'è già uno di questi. */
  unless?: string[];
}

/** Le opzioni che l'ospite sceglie prima di pagare. */
export interface ExtraCheckoutDetails {
  selectedTime?: string;
  cleaningVariant?: string;
  /** Id della confezione scelta per "Caffè & Brioche" (il prezzo lo fa lei). */
  bundleVariant?: string;
  /** Id dell'articolo scelto (carta, igiene, kit, biancheria): il prezzo lo fa lui. */
  itemVariant?: string;
  /** Il Cestino della Nonna: id della bottiglia scelta (il prezzo non cambia). */
  kitDrink?: string;
  cleaningDates?: string[];
  wineSelections?: Array<{ id: string; qty: number }>;
  notes?: string;
  // --- Transfer: stessi nomi che il sito salva sulla prenotazione, così
  // email, ricevute e pannelli del gestore leggono una cosa sola. ---
  isArrival?: boolean;
  isDeparture?: boolean;
  arrivalDateTime?: string;
  departureDateTime?: string;
  arrivalTime?: string;
  departureTime?: string;
  stationName?: string;
  departureStationName?: string;
  flightNumber?: string;
  departureFlightNumber?: string;
  // --- Taxi su misura (richiesta): meta, giorno, ora, persone, A/R ---
  destination?: string;
  transferDate?: string;
  transferTime?: string;
  people?: number;
  roundTrip?: boolean;
  returnTime?: string;
}

export interface ExtraCheckoutResponse {
  success: boolean;
  clientSecret?: string;
  publishableKey?: string;
  amount?: number;
  paymentId?: string;
  error?: string;
  errorCode?: string;
  /**
   * Scorta fisica (la culla): quanti pezzi restano DAVVERO per queste date,
   * secondo il server, quando la quantità chiesta era troppa
   * (`inventory_insufficient`). Serve al messaggio ("ne restano 2") e a
   * riportare lo stepper entro quel numero.
   */
  availableUnits?: number;
  /** Magazzino: 'consumable' = un prodotto contato (bottiglia, snack…) finito o quasi. */
  stockKind?: string;
  /** Ordine minimo dei prodotti (`min_order_not_reached`): il minimo e quanto manca. */
  minOrder?: number;
  missing?: number;
}

export interface ExtraRequestResponse {
  success: boolean;
  alreadyRequested?: boolean;
  status?: string;
  message?: string;
  error?: string;
  errorCode?: string;
}

// ============================================
// RECEIPTS (ricevute) INTERFACES
// ============================================

export type ReceiptType = 'stay' | 'extras' | 'tourist_tax' | 'gift_card';

export interface ReceiptInfo {
  id: string;
  number: string;
  type: ReceiptType;
  documentType: string;
  total: number;
  issuedAt: string | null;
  // Sempre null negli elenchi (info / receipt): il link arriva solo da
  // unlockReceiptDownloads dopo la verifica dell'email della prenotazione.
  downloadUrl: string | null;
  // PDF pronto e link generabile (dopo la verifica email).
  canDownload?: boolean;
  pdfReady: boolean;
  sent: boolean;
}

export type ReceiptRequestReason =
  | 'ok'
  | 'already_issued'
  | 'excluded'
  | 'before_checkin'
  | 'window_closed'
  | 'platform_disabled';

export type ReceiptIssueBlocker = 'none' | 'awaiting_reconciliation' | 'awaiting_payment' | 'awaiting_price';

export interface ReceiptRequestState {
  canRequest: boolean;
  reason: ReceiptRequestReason;
  hasStayReceipt: boolean;
  blocker: ReceiptIssueBlocker;
  request: {
    requestedAt: string;
    status: 'pending' | 'issued';
    holderName: string | null;
  } | null;
  /**
   * L'intestatario gia' salvato (sul sito, nell'app o dallo staff) per
   * pre-compilare il modulo. Assente con un channel manager vecchio.
   */
  holder?: ReceiptHolderInput | null;
}

// Intestatario scelto dall'ospite (validato dal channel manager)
export interface ReceiptHolderInput {
  name?: string;
  address?: string;
  city?: string;
  zip?: string;
  province?: string;
  country?: string;
  taxCode?: string;
  vatNumber?: string;
  email?: string;
}

export interface ReceiptsResponse {
  success: boolean;
  receipts?: ReceiptInfo[];
  receiptRequest?: ReceiptRequestState | null;
  issued?: boolean;
  error?: string;
  errorCode?: string;
  // Codici errore per campo (name_required, tax_code_invalid, …)
  holderErrors?: Record<string, string>;
}

// ============================================
// TOURIST TAX (imposta di soggiorno) INTERFACES
// ============================================

// Un appartamento dentro una prenotazione di gruppo: quota e stato suoi.
export interface TouristTaxGroupApartment {
  apartmentName: string;
  amount: number;
  status: 'requested' | 'paid';
}

export interface TouristTaxInfo {
  status: 'requested' | 'paid';
  // Su una prenotazione di gruppo è il TOTALE ancora dovuto per tutti gli
  // appartamenti, non la quota della singola prenotazione del token: il
  // pagamento è unico (tutto-o-niente).
  amount: number;
  requestedAt: string | null;
  paidAt: string | null;
  // Numero quietanza (solo pagamenti Stripe, dopo il webhook). Su un gruppo
  // sono più numeri separati da virgola: una quietanza per appartamento.
  receiptNumber: string | null;
  // true finché la richiesta è aperta: abilita il checkout embedded in app
  canPayInApp: boolean;
  // Pagina pubblica del channel manager: fallback se Stripe non carica in app
  payUrl: string | null;
  /**
   * Presente solo sulle prenotazioni di gruppo (più appartamenti, un unico
   * pagamento). Assente = prenotazione singola, comportamento identico a
   * prima. Serve alla card per dire "per N appartamenti" ed elencare le
   * quote, così l'ospite capisce che sta pagando per tutti.
   */
  group?: {
    total: number;
    paidCount: number;
    apartments: TouristTaxGroupApartment[];
  };
}

export interface TouristTaxStatusResponse {
  success: boolean;
  touristTax?: TouristTaxInfo | null;
  error?: string;
  errorCode?: string;
}

export interface TouristTaxCheckoutResponse {
  success: boolean;
  clientSecret?: string;
  publishableKey?: string;
  amount?: number;
  // Valorizzato nelle risposte di errore (es. already_paid) per aggiornare la card
  touristTax?: TouristTaxInfo | null;
  error?: string;
  errorCode?: string;
}

// ============================================
// GROUP BOOKING INTERFACES
// ============================================

// ============================================
// CLIMATE (condizionatore dell'appartamento, comandabile dal WiFi di casa)
// ============================================

export type ClimateMode = 'auto' | 'cool' | 'dry' | 'heat' | 'fan_only';
export type ClimateFan = 'auto' | 'low' | 'medium' | 'high' | 'silent';

export interface ClimateCommand {
  power?: boolean;
  mode?: ClimateMode;
  targetTemp?: number;
  fanSpeed?: ClimateFan;
}

export interface ClimateDevice {
  id: string;
  name: string;
  roomName: string | null;
  type: 'ac' | 'dehumidifier';
  // Il ponte (tablet di casa) l'ha letto negli ultimi 10 minuti
  online: boolean;
  power: boolean | null;
  mode: ClimateMode | null;
  targetTemp: number | null;
  indoorTemp: number | null;
  fanSpeed: ClimateFan | null;
  minTemp: number;
  maxTemp: number;
  // Comando in coda non ancora eseguito dal ponte
  pending: boolean;
  pendingCommand: ClimateCommand | null;
  lastCommandError: string | null;
  lastStateAt: string | null;
}

export interface ClimateApartment {
  apartmentId: string;
  apartmentName: string;
  devices: ClimateDevice[];
}

export interface ClimateResponse {
  success: boolean;
  // Almeno un condizionatore comandabile dall'ospite nelle case della prenotazione
  available?: boolean;
  // La richiesta arriva dalla connessione dell'appartamento
  onHomeNetwork?: boolean;
  // Oggi è fra il check-in e il check-out
  stayActive?: boolean;
  wifiName?: string;
  // Vuoto quando non si può comandare
  apartments?: ClimateApartment[];
  error?: string;
  errorCode?: string;
}

export interface ClimateCommandResponse {
  success: boolean;
  device?: ClimateDevice;
  error?: string;
  // 'not_on_home_network' | 'stay_not_active' (403), altrimenti 400/404
  errorCode?: string;
}

export interface GroupGuest {
  firstName: string;
  lastName: string;
  isMainGuest?: boolean;
}

export interface GroupApartment {
  bookingId: string;
  apartmentName: string;
  apartmentAddress?: string;
  apartmentLatitude?: number | null;
  apartmentLongitude?: number | null;
  numberOfGuests: number;
  guests: GroupGuest[];
  onlineCheckInCompleted: boolean;
  accessCode: string | null;
}

export interface GroupBookingResponse {
  success: boolean;
  groupBookingId?: string;
  bookingReference?: string;
  guestName?: string;
  guestEmail?: string;
  checkIn?: string;
  checkOut?: string;
  totalGuests?: number;
  totalPrice?: number;
  allCheckedIn?: boolean;
  sharedAccessCode?: string | null;
  apartments?: GroupApartment[];
  checkInUrl?: string;
  error?: string;
  /** Codice stabile (es. SESSION_EXPIRED, booking_*): e' questo che il codice confronta, non il testo tradotto. */
  errorCode?: string;
}

export interface AccessCodeApartment {
  apartmentName: string;
  apartmentAddress?: string;
  apartmentLatitude?: number | null;
  apartmentLongitude?: number | null;
  accessCode: string;
  validFrom: string;
  validUntil: string;
}

export interface AccessCodeResponse {
  success: boolean;
  accessCode?: string;
  apartmentName?: string;
  apartmentAddress?: string;
  apartmentLatitude?: number | null;
  apartmentLongitude?: number | null;
  validFrom?: string;
  validUntil?: string;
  isGroupBooking?: boolean;
  apartments?: AccessCodeApartment[];
  error?: string;
  /** Codice stabile (es. SESSION_EXPIRED, booking_*): e' questo che il codice confronta, non il testo tradotto. */
  errorCode?: string;
}

export interface GuestAuthResponse {
  success: boolean;
  token?: string;
  booking?: GuestBooking;
  error?: string;
  errorCode?: string;
}

// Channel manager error code prefix for booking-level access denials
// (no-show, cancelled+refunded, cancelled). The prefix is contractually
// stable per the channel manager team — any future booking_* code is
// treated as an invalidated booking by this client without releases.
export const BOOKING_INVALIDATED_CODE_PREFIX = 'booking_';

interface ErrorBody {
  error?: string;
  errorCode?: string;
}

const extractErrorBody = (data: unknown): ErrorBody => {
  if (data && typeof data === 'object') {
    const body = data as ErrorBody;
    return {
      error: typeof body.error === 'string' ? body.error : undefined,
      errorCode: typeof body.errorCode === 'string' ? body.errorCode : undefined,
    };
  }
  return {};
};

export const isBookingInvalidatedCode = (code: string | undefined): boolean =>
  typeof code === 'string' && code.startsWith(BOOKING_INVALIDATED_CODE_PREFIX);

/**
 * When a JWT-protected endpoint returns 403 with a booking_* errorCode,
 * the booking has been invalidated server-side and the JWT is effectively
 * dead (re-login with the same booking will keep failing). Fire a global
 * window event so the App-level BookingInvalidatedDialog can take over
 * and the active useGuestSession instances can clear local state.
 *
 * Login attempts (`authenticateGuest`) intentionally don't dispatch — the
 * inline alert in the login modal is enough; we don't want a modal
 * stacked on top of the login modal.
 */
const dispatchBookingInvalidatedIfNeeded = (
  status: number,
  errorCode: string | undefined
): void => {
  if (typeof window === 'undefined') return;
  if (status !== 403) return;
  if (!isBookingInvalidatedCode(errorCode)) return;
  window.dispatchEvent(new CustomEvent('nva_booking_invalidated', {
    detail: { errorCode },
  }));
};

export const EMOJI_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;
export type ReactionEmoji = typeof EMOJI_REACTIONS[number];

export interface MessageReaction {
  emoji: string;
  senderType: 'guest' | 'admin';
  senderName: string;
  createdAt: string;
}

/**
 * Una foto in chat (caricata su Cloudinary dal channel manager). `thumbUrl`
 * e' la miniatura per la bolla, `url` la foto intera per il visore. Una foto
 * cancellata dallo staff resta nel messaggio con `url` vuoto e `deletedAt`:
 * al suo posto si mostra "Foto rimossa".
 */
export interface ChatAttachment {
  kind: 'image';
  url: string;
  thumbUrl: string;
  width: number;
  height: number;
  bytes: number;
  mime: string;
  publicId: string;
  deletedAt?: string | null;
}

/** Il testo che il server mette ai messaggi di sole foto. */
export const CHAT_PHOTO_ONLY_TEXT = '📷';

/** Foto piu' grandi di cosi' il server le rifiuta (anche dopo la compressione). */
export const CHAT_PHOTO_MAX_BYTES = 4 * 1024 * 1024;

/**
 * Il testo da mostrare per un caricamento fallito, dal codice d'errore: il
 * messaggio del server e' in italiano, l'app parla la lingua dell'ospite.
 */
export const chatPhotoErrorKey = (errorCode?: string): string => {
  if (errorCode === 'file_too_large') return 'guestChat.photo.tooLarge';
  if (errorCode === 'unsupported_format') return 'guestChat.photo.unsupported';
  if (errorCode === 'rate_limited') return 'guestChat.photo.rateLimited';
  return 'guestChat.photo.failed';
};

/** Foto troppo grande o formato sbagliato: riprovare non serve, la si puo' solo togliere. */
export const isChatPhotoErrorRetryable = (errorCode?: string): boolean =>
  errorCode !== 'file_too_large' && errorCode !== 'unsupported_format';

export interface ChatMessage {
  id: string;
  type: 'email' | 'chat';
  senderType: 'admin' | 'guest' | 'system';
  senderName: string;
  subject?: string;
  message: string;
  /**
   * 'welcome' = il benvenuto all'ingresso (pulsanti clima/lavatrice/WiFi);
   * 'delivery' / 'delivered' = orario di consegna di un ordine (card con orario e articoli);
   * 'order' = ordine appena ricevuto (card con gli articoli, prima dell'orario).
   */
  kind?: 'welcome' | 'delivery' | 'delivered' | 'order' | string;
  /** Dati strutturati dei messaggi speciali (per 'delivery': deliveryAt, items, note, status). */
  meta?: ChatMessageMeta;
  /**
   * La lingua in cui il server ha scritto il messaggio ("de"), quando la
   * manda. Le card la usano per non tradurre verso la loro stessa lingua.
   */
  locale?: string;
  previewText?: string;
  isRead: boolean;
  /**
   * Quando il destinatario l'ha letto. Sui messaggi dell'ospite e' la
   * lettura dello staff: accende le spunte blu. Opzionale perche' i server
   * meno recenti non lo mandano.
   */
  readAt?: string | null;
  createdAt: string;
  reactions?: MessageReaction[];
  /** Foto del messaggio (ospite o staff). Assente sui server che non le gestiscono ancora. */
  attachments?: ChatAttachment[];
}

export interface ChatMessageMeta {
  /** 'reminder' = promemoria prima della consegna: card come quella programmata, col testo del promemoria. */
  status?: 'scheduled' | 'delivered' | 'reminder' | 'new' | string;
  deliveryAt?: string;
  deliveredAt?: string;
  items?: Array<{ serviceId?: string; qty?: number; label: string }>;
  note?: string;
  apartmentName?: string;
  /** Solo per 'order': quando l'ospite ha ordinato. */
  orderedAt?: string;
  /** Solo per 'order': totale dell'ordine. */
  total?: number;
  /** Solo per 'order': true se c'e' qualcosa da consegnare (arrivera' un orario). */
  deliverable?: boolean;
  [key: string]: unknown;
}

export interface ChatMessagesResponse {
  success: boolean;
  messages?: ChatMessage[];
  /**
   * Messaggi dello staff non ancora letti, contati dal server. Il badge usa
   * questo quando c'e': i server meno recenti non lo mandano.
   */
  unreadCount?: number;
  error?: string;
  errorCode?: string;
}

export interface SendMessageResponse {
  success: boolean;
  message?: ChatMessage;
  error?: string;
  errorCode?: string;
}

export interface UploadChatPhotoResponse {
  success: boolean;
  attachment?: ChatAttachment;
  error?: string;
  errorCode?: string;
}

export interface ReactionResponse {
  success: boolean;
  action?: 'added' | 'removed' | 'replaced';
  reactions?: MessageReaction[];
  error?: string;
  errorCode?: string;
}

/**
 * Authenticate guest with booking reference and email
 * Optionally sends deviceId for push notifications
 */
export const authenticateGuest = async (
  bookingReference: string,
  email: string,
  deviceId?: string | null
): Promise<GuestAuthResponse> => {
  try {
    // La lingua si manda solo se l'ospite l'ha scelta a mano: qui l'app e'
    // ancora nella lingua del telefono, quella del check-in si applica dopo
    // il login. Senza `lang` il server tiene la lingua del check-in.
    const manual = manualLanguage();
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/chat/auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bookingReference,
        email,
        ...(deviceId && { deviceId }),  // Include deviceId if available
        // Da qui in poi chat, email e push arrivano in questa lingua.
        ...(manual && { lang: manual }),
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      // Login attempts: don't dispatch the global event — the login modal
      // shows the message inline (no popup on top of popup).
      return {
        success: false,
        error: error || tr('apiErrors.authenticationFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      token: data.token,
      booking: data.booking,
    };
  } catch (error) {
    console.error('Guest auth error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

/**
 * L'esito del riscatto di una richiesta di accesso automatico dopo il
 * check-in online (POST /api/public/checkin/claim, lib/checkInClaimStorage):
 * - ready: token e prenotazione (come chat/auth, ma senza il codice della
 *   porta); la richiesta è consumata;
 * - pending: il check-in aspetta lo smistamento, si riprova più tardi;
 * - not_found: sconosciuta, scaduta, già usata o check-in cancellato (il
 *   channel manager risponde sempre allo stesso modo); anche un channel
 *   manager vecchio, senza la rotta;
 * - denied: prenotazione no-show o cancellata (booking_*);
 * - invalid: segreto scritto male;
 * - rate_limited: troppi "non trovato" da questo indirizzo, si aspetta;
 * - error: rete o server, si riprova al prossimo giro.
 */
export type CheckInClaimResult =
  | { status: 'ready'; token: string; booking: GuestBooking }
  | { status: 'pending' }
  | { status: 'not_found' }
  | { status: 'denied'; errorCode: string; error?: string }
  | { status: 'invalid' }
  | { status: 'rate_limited'; retryAfterSec: number }
  | { status: 'error' };

/** Senza Retry-After leggibile (CORS) si aspetta quanto il blocco del server: 10 minuti. */
const CLAIM_DEFAULT_RETRY_SEC = 600;

/**
 * Riscatta una richiesta di accesso automatico. Il segreto viaggia solo qui,
 * nel corpo della POST. Non lancia mai. Come nel login, la lingua si manda
 * solo se l'ospite l'ha scelta a mano (il server la salva per chat e push).
 * Non manda l'evento globale delle prenotazioni annullate: lo decide il
 * watcher, che sa di quale richiesta si tratta.
 */
export const redeemCheckInClaim = async (
  claim: string,
  deviceId?: string | null,
): Promise<CheckInClaimResult> => {
  try {
    const manual = manualLanguage();
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/checkin/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        claim,
        ...(deviceId && { deviceId }),
        ...(manual && { lang: manual }),
      }),
    });

    let data: Record<string, unknown> | null = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (response.status === 429) {
      const retry = parseInt(response.headers.get('Retry-After') || '', 10);
      return { status: 'rate_limited', retryAfterSec: retry > 0 ? retry : CLAIM_DEFAULT_RETRY_SEC };
    }
    if (response.status === 404) return { status: 'not_found' };
    if (response.status === 400) return { status: 'invalid' };
    if (response.status === 403) {
      const errorCode = typeof data?.errorCode === 'string' ? data.errorCode : undefined;
      if (errorCode && isBookingInvalidatedCode(errorCode)) {
        return { status: 'denied', errorCode, error: typeof data?.error === 'string' ? data.error : undefined };
      }
      return { status: 'error' };
    }
    if (!response.ok || !data || data.success !== true) return { status: 'error' };
    if (data.status === 'pending') return { status: 'pending' };
    if (data.status === 'ready' && typeof data.token === 'string' && data.token && data.booking && typeof data.booking === 'object') {
      return { status: 'ready', token: data.token, booking: data.booking as GuestBooking };
    }
    return { status: 'error' };
  } catch (error) {
    console.error('Check-in claim error:', error);
    return { status: 'error' };
  }
};

/**
 * L'ospite ha cambiato lingua a mano: la si dice al server, cosi' chat,
 * email e push arrivano nella lingua nuova. Tentativo e basta: se non
 * riesce non si blocca niente, e non lancia mai.
 */
export const setGuestLocale = async (token: string, lang: string): Promise<boolean> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/chat/locale`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lang: appLanguageOf(lang) }),
    });
    return response.ok;
  } catch (error) {
    console.error('Set guest locale error:', error);
    return false;
  }
};

/**
 * Fetch chat messages (emails + chat messages merged)
 */
export const fetchChatMessages = async (token: string): Promise<ChatMessagesResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/chat/messages`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      // Token expired or invalid
      if (response.status === 401) {
        return {
          success: false,
          error: tr('apiErrors.sessionExpired'),
          errorCode: 'SESSION_EXPIRED',
        };
      }
      return {
        success: false,
        error: error || tr('apiErrors.loadMessagesFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      messages: data.messages || [],
      unreadCount: typeof data.unreadCount === 'number' ? data.unreadCount : undefined,
    };
  } catch (error) {
    console.error('Fetch messages error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

/**
 * Send a chat message from guest to admin
 */
export const sendChatMessage = async (
  token: string,
  message: string,
  attachments?: ChatAttachment[]
): Promise<SendMessageResponse> => {
  try {
    // Con le foto il testo puo' essere vuoto: il server mette "📷".
    const body: { message: string; lang: string; attachments?: ChatAttachment[] } = {
      message,
      lang: appLanguageOf(i18n.language),
    };
    if (attachments && attachments.length > 0) body.attachments = attachments;
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/chat/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      if (response.status === 401) {
        return {
          success: false,
          error: tr('apiErrors.sessionExpired'),
          errorCode: 'SESSION_EXPIRED',
        };
      }
      return {
        success: false,
        error: error || tr('guestChat.sendError'),
        errorCode,
      };
    }

    return {
      success: true,
      message: data.message,
    };
  } catch (error) {
    console.error('Send message error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

// Oltre questo tempo il caricamento si abbandona: su una rete da spiaggia
// meglio un "tocca per riprovare" che una rotella infinita.
const CHAT_UPLOAD_TIMEOUT_MS = 60000;

/** Legge un allegato dalla risposta del server, scartando quello che non torna. */
const toChatAttachment = (raw: unknown): ChatAttachment | null => {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Partial<ChatAttachment>;
  if (typeof a.url !== 'string' || !a.url) return null;
  return {
    kind: 'image',
    url: a.url,
    thumbUrl: typeof a.thumbUrl === 'string' && a.thumbUrl ? a.thumbUrl : a.url,
    width: typeof a.width === 'number' ? a.width : 0,
    height: typeof a.height === 'number' ? a.height : 0,
    bytes: typeof a.bytes === 'number' ? a.bytes : 0,
    mime: typeof a.mime === 'string' ? a.mime : '',
    publicId: typeof a.publicId === 'string' ? a.publicId : '',
    deletedAt: a.deletedAt ?? null,
  };
};

/**
 * Carica una foto della chat (gia' compressa dall'app, o l'originale HEIC:
 * la converte Cloudinary). Restituisce l'allegato da spedire poi con
 * sendChatMessage. Niente Content-Type a mano: con FormData lo mette il
 * browser, con il boundary giusto.
 */
export const uploadChatPhoto = async (
  token: string,
  file: Blob,
  filename: string
): Promise<UploadChatPhotoResponse> => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), CHAT_UPLOAD_TIMEOUT_MS) : null;
  try {
    const form = new FormData();
    form.append('file', file, filename);
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/chat/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: form,
      signal: controller ? controller.signal : undefined,
    });

    // Un 413 del proxy arriva come pagina HTML, non JSON.
    const data = await response.json().catch(() => ({} as Record<string, unknown>));

    if (!response.ok) {
      const { errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      if (response.status === 401) {
        return { success: false, error: tr('apiErrors.sessionExpired'), errorCode: 'SESSION_EXPIRED' };
      }
      // Il testo del server (in italiano) non si mostra: conta il codice, e
      // se manca (proxy, server meno recenti) lo si deduce dallo stato HTTP.
      const code = errorCode
        || (response.status === 413 ? 'file_too_large'
          : response.status === 429 ? 'rate_limited'
          : response.status === 400 ? 'bad_request'
          : 'upload_failed');
      return { success: false, error: tr(chatPhotoErrorKey(code)), errorCode: code };
    }

    const attachment = toChatAttachment((data as { attachment?: unknown }).attachment);
    if (!attachment) {
      return { success: false, error: tr('guestChat.photo.failed'), errorCode: 'upload_failed' };
    }
    return { success: true, attachment };
  } catch (error) {
    console.error('Upload chat photo error:', error);
    return { success: false, error: tr('accessCode.errors.connectionError'), errorCode: 'upload_failed' };
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
};

// ============================================
// CONSEGNA IN CORSO (il widget in cima a tutte le pagine)
// ============================================

/**
 * La consegna "attiva" della prenotazione, come la decide il channel
 * manager: quella programmata piu' vicina, oppure l'ultima consegnata da
 * meno di due ore. `null` = niente da mostrare.
 */
export interface ActiveDelivery {
  status: 'scheduled' | 'delivered';
  /** Orario fissato dallo staff (ISO). */
  at: string | null;
  /** Quando e' stata segnata consegnata (ISO). */
  deliveredAt: string | null;
  /** Gli articoli, gia' nella lingua dell'ospite. */
  items: string[];
  note: string | null;
  updatedAt: string;
}

export interface ActiveDeliveryResponse {
  success: boolean;
  delivery: ActiveDelivery | null;
  error?: string;
  errorCode?: string;
}

const isoOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value) return null;
  return isNaN(Date.parse(value)) ? null : value;
};

/**
 * Il server e' nuovo: si accetta solo quello che si sa leggere. Uno stato
 * sconosciuto, o una consegna programmata senza orario, vale "niente".
 */
const normalizeActiveDelivery = (raw: unknown): ActiveDelivery | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const status = d.status === 'delivered' ? 'delivered' : d.status === 'scheduled' ? 'scheduled' : null;
  if (!status) return null;
  const at = isoOrNull(d.at);
  const deliveredAt = isoOrNull(d.deliveredAt);
  if (status === 'scheduled' && !at) return null;
  const items: string[] = [];
  if (Array.isArray(d.items)) {
    d.items.forEach((it) => {
      // Stringhe come da contratto; per sicurezza anche { label } come nelle card della chat.
      const label = typeof it === 'string'
        ? it
        : it && typeof it === 'object' && typeof (it as { label?: unknown }).label === 'string'
          ? (it as { label: string }).label
          : '';
      if (label.trim()) items.push(label.trim());
    });
  }
  return {
    status,
    at,
    deliveredAt,
    items,
    note: typeof d.note === 'string' && d.note.trim() ? d.note.trim() : null,
    updatedAt: isoOrNull(d.updatedAt) || deliveredAt || at || new Date().toISOString(),
  };
};

/** GET /api/public/booking/delivery: la consegna da mostrare nel widget. */
export const fetchActiveDelivery = async (token: string): Promise<ActiveDeliveryResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/delivery`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Un channel manager che non ha ancora l'endpoint risponde 404 (magari
    // in HTML): non e' un errore da mostrare, semplicemente niente consegna.
    let data: unknown = null;
    try { data = await response.json(); } catch { data = null; }
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        delivery: null,
        error,
        errorCode: response.status === 401 ? 'SESSION_EXPIRED' : errorCode || `http_${response.status}`,
      };
    }
    const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    return { success: body.success !== false, delivery: normalizeActiveDelivery(body.delivery) };
  } catch (error) {
    console.error('Error fetching active delivery:', error);
    return { success: false, delivery: null, error: tr('apiErrors.networkError') };
  }
};

// ============================================
// RITIRO RIFIUTI (stessa uscita di tablet e tag NFC nel channel manager)
// ============================================

export interface TrashWindowInfo {
  state: 'open' | 'before_window' | 'after_window';
  canRequest: boolean;
  day: string;
  opensDay: string | null;
  from: string;
  to: string;
}

export interface TrashPickupStatus {
  success: boolean;
  /** false = prenotazione senza appartamento: si scrive in chat. */
  available: boolean;
  window?: TrashWindowInfo;
  /** Richiesta gia' aperta (oggi, o prenotata per domani). */
  open?: { requestId: string; requestedAt: string; day: string; isToday: boolean } | null;
  isCheckoutDay?: boolean;
  today?: string;
  error?: string;
  errorCode?: string;
}

export interface TrashPickupRequestInput {
  types: string[];
  bags: number;
  note?: string;
  /** Fuori orario: prenota per la prossima finestra utile. */
  schedule?: boolean;
}

export interface TrashPickupResult extends TrashPickupStatus {
  created?: boolean;
  requestedAt?: string;
  scheduledFor?: string | null;
  code?: 'window_closed' | 'notify_failed' | 'types_required' | 'apartment_unknown';
}

export const fetchTrashPickup = async (token: string): Promise<TrashPickupStatus> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/trash-pickup`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return { success: false, available: false, error: error || tr('apiErrors.trashPickupUnavailable'), errorCode };
    }
    return { success: true, available: data?.available !== false, window: data?.window, open: data?.open ?? null, isCheckoutDay: !!data?.isCheckoutDay, today: data?.today };
  } catch (error) {
    console.error('Error fetching trash pickup:', error);
    return { success: false, available: false, error: tr('apiErrors.networkError') };
  }
};

export const requestTrashPickup = async (token: string, input: TrashPickupRequestInput): Promise<TrashPickupResult> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/trash-pickup`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return { success: false, available: true, code: data?.code, window: data?.window, error: error || tr('apiErrors.requestFailed'), errorCode };
    }
    return {
      success: true,
      available: true,
      created: !!data?.created,
      requestedAt: data?.requestedAt,
      scheduledFor: data?.scheduledFor ?? null,
      window: data?.window,
      open: data?.open ?? null,
      isCheckoutDay: !!data?.isCheckoutDay,
      today: data?.today,
    };
  } catch (error) {
    console.error('Error requesting trash pickup:', error);
    return { success: false, available: true, error: tr('apiErrors.networkError') };
  }
};

export interface ExcursionRequestPayload {
  /** Il testo gia' composto nella lingua dell'ospite (finisce in chat cosi' com'e'). */
  message: string;
  date: string;
  towns: string[];
  people: number;
  notes?: string;
}

/**
 * L'escursione su misura: chat + campanella dedicata nel channel manager.
 * Se il server non ha ancora la rotta (404), parte come semplice messaggio
 * in chat: la richiesta arriva comunque.
 */
export const sendExcursionRequest = async (
  token: string,
  payload: ExcursionRequestPayload
): Promise<SendMessageResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/excursion-request`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (response.status === 404) return sendChatMessage(token, payload.message);
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return { success: false, error: error || tr('romanticWeek.errors.sendFailed'), errorCode };
    }
    return { success: true, message: data.message };
  } catch (error) {
    console.error('Send excursion request error:', error);
    return { success: false, error: tr('accessCode.errors.connectionError') };
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/chat/messages/read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({} as Record<string, unknown>));
      const { errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
    }

    return response.ok;
  } catch (error) {
    console.error('Mark read error:', error);
    return false;
  }
};

/**
 * Add / replace / remove a reaction on a chat message.
 *
 * Server semantics (last-write-wins, no optimistic-locking conflict):
 *   - Tap the same emoji that's already active on the guest side -> action: "removed"
 *   - Tap a different emoji while the guest already reacted     -> action: "replaced"
 *   - Tap an emoji with no prior guest reaction                 -> action: "added"
 *
 * The response always carries the authoritative full reactions array for the
 * message (both guest + admin sides). Clients should trust it over local state.
 */
export const sendReaction = async (
  token: string,
  messageId: string,
  emoji: string
): Promise<ReactionResponse> => {
  try {
    const response = await fetch(
      `${CHANNEL_MANAGER_URL}/api/public/chat/messages/${encodeURIComponent(messageId)}/reactions`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emoji }),
      }
    );

    const data = await response.json().catch(() => ({} as Record<string, unknown>));

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      if (response.status === 401) {
        return { success: false, error: tr('apiErrors.sessionExpired'), errorCode: 'SESSION_EXPIRED' };
      }
      return {
        success: false,
        error: error || tr('apiErrors.reactionFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      action: (data as ReactionResponse).action,
      reactions: (data as ReactionResponse).reactions || [],
    };
  } catch (error) {
    console.error('Send reaction error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

/**
 * Decode JWT payload without verification
 * Used for auto-login from email link where token is already trusted
 */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
};

/**
 * Get booking info for logged in guest
 * First tries to decode from JWT, falls back to API call
 */
export const fetchBookingInfo = async (token: string): Promise<GuestAuthResponse> => {
  // Try to decode JWT payload directly (for auto-login tokens from email)
  const payload = decodeJwtPayload(token);

  if (payload && payload.bookingId) {
    console.log('📦 Decoded booking info from JWT payload');
    const isGroup = !!(payload.isGroupBooking && payload.groupBookingId);
    if (isGroup) {
      console.log('👥 Group booking detected:', payload.groupBookingId);
    }
    return {
      success: true,
      booking: {
        id: payload.bookingId as string,
        bookingReference: (payload.bookingReference as string) || '',
        guestName: (payload.guestName as string) || 'Guest',
        guestEmail: (payload.guestEmail as string) || '',
        apartmentName: (payload.apartmentName as string) || '',
        checkIn: (payload.checkIn as string) || '',
        checkOut: (payload.checkOut as string) || '',
        numberOfGuests: (payload.numberOfGuests as number) || 1,
        status: 'confirmed',
        onlineCheckInCompleted: (payload.onlineCheckInCompleted as boolean) || false,
        // Group booking fields
        isGroupBooking: isGroup,
        groupBookingId: isGroup ? (payload.groupBookingId as string) : undefined,
        // Preserve undefined for OTA/legacy bookings — only set when explicitly boolean
        isNonRefundable: typeof payload.isNonRefundable === 'boolean'
          ? (payload.isNonRefundable as boolean)
          : undefined,
      },
    };
  }

  // Fallback: try API call for tokens that don't contain booking info
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/info`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('apiErrors.bookingInfoFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      booking: data.booking,
    };
  } catch (error) {
    console.error('Fetch booking error:', error);
    return {
      success: false,
      error: tr('apiErrors.connectionErrorShort'),
    };
  }
};

/**
 * Fetch fresh booking info from API (forces API call, ignores JWT)
 * Use this to get updated check-in status
 */
export const fetchBookingInfoFromApi = async (token: string): Promise<GuestAuthResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/info`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      if (response.status === 401) {
        return {
          success: false,
          error: tr('apiErrors.sessionExpired'),
          errorCode: 'SESSION_EXPIRED',
        };
      }
      return {
        success: false,
        error: error || tr('apiErrors.bookingInfoFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      booking: data.booking,
    };
  } catch (error) {
    console.error('Fetch booking info error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

/**
 * Stato LIVE del volo d'arrivo agganciato al transfer aeroporto.
 * Endpoint leggero pensato per il polling del widget volo (il giorno
 * dell'arrivo). Restituisce { hasFlight:false } quando non c'è un volo.
 * Gestisce i 403 booking_* come le altre chiamate (logout dell'app).
 */
export const fetchFlightStatus = async (token: string): Promise<FlightStatusResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/flight`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        hasFlight: false,
        error: error || tr('apiErrors.flightStatusFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      hasFlight: Boolean(data.hasFlight),
      flight: data.flight,
      departure: data.departure,
    };
  } catch (error) {
    console.error('Fetch flight status error:', error);
    return {
      success: false,
      hasFlight: false,
      error: tr('apiErrors.connectionErrorShort'),
    };
  }
};

/**
 * Fetch access code with email verification
 * Requires email to match booking for security
 */
export const fetchAccessCode = async (
  token: string,
  email: string
): Promise<AccessCodeResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/access-code`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      if (response.status === 401) {
        return {
          success: false,
          error: tr('apiErrors.sessionExpired'),
          errorCode: 'SESSION_EXPIRED',
        };
      }
      // Dopo il check-out il server non consegna piu' il codice (403
      // stay_not_active): il messaggio nella lingua dell'app, non il testo
      // italiano del server.
      if (errorCode === 'stay_not_active') {
        return { success: false, error: tr('myStay.home.afterStay'), errorCode };
      }
      return {
        success: false,
        error: error || tr('apiErrors.accessCodeFetchFailed'),
        errorCode,
      };
    }

    return data;
  } catch (error) {
    console.error('Fetch access code error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

/**
 * Fetch group booking information
 * Returns all apartments in the group with their check-in status
 */
export const fetchGroupBookingInfo = async (
  token: string,
  groupBookingId: string
): Promise<GroupBookingResponse> => {
  try {
    const response = await fetch(
      `${CHANNEL_MANAGER_URL}/api/public/guest/group-bookings?groupBookingId=${encodeURIComponent(groupBookingId)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      if (response.status === 401) {
        return {
          success: false,
          error: tr('apiErrors.sessionExpired'),
          errorCode: 'SESSION_EXPIRED',
        };
      }
      return {
        success: false,
        error: error || tr('apiErrors.groupBookingFetchFailed'),
      };
    }

    return {
      success: true,
      ...data,
    };
  } catch (error) {
    console.error('Fetch group booking error:', error);
    return {
      success: false,
      error: tr('accessCode.errors.connectionError'),
    };
  }
};

// ============================================
// TOURIST TAX (imposta di soggiorno)
// ============================================

/**
 * Stato aggiornato dell'imposta di soggiorno. Usato dopo il pagamento in app
 * per aspettare che il webhook Stripe segni 'paid' (l'app non decide mai da
 * sola che è pagata). Gestisce i 403 booking_* come le altre chiamate.
 */
export const fetchTouristTaxStatus = async (token: string): Promise<TouristTaxStatusResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/tourist-tax`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('apiErrors.touristTaxStatusFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      touristTax: data.touristTax ?? null,
    };
  } catch (error) {
    console.error('Fetch tourist tax status error:', error);
    return {
      success: false,
      error: tr('apiErrors.connectionErrorShort'),
    };
  }
};

/**
 * Crea la Checkout Session Stripe EMBEDDED per l'imposta di soggiorno e
 * restituisce client_secret + publishable key. L'identità viene dal token,
 * l'importo dal channel manager: l'app non manda nulla nel body.
 */
export const createTouristTaxCheckout = async (token: string): Promise<TouristTaxCheckoutResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/tourist-tax`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('apiErrors.touristTaxPaymentFailed'),
        errorCode,
        touristTax: data && typeof data === 'object' ? (data.touristTax ?? null) : null,
      };
    }

    return {
      success: true,
      clientSecret: data.clientSecret,
      publishableKey: data.publishableKey,
      amount: data.amount,
    };
  } catch (error) {
    console.error('Create tourist tax checkout error:', error);
    return {
      success: false,
      error: tr('apiErrors.connectionErrorShort'),
    };
  }
};

// ============================================
// RECEIPTS (ricevute del soggiorno)
// ============================================

const parseReceiptsResponse = async (response: Response): Promise<ReceiptsResponse> => {
  const data = await response.json();
  if (!response.ok) {
    const { error, errorCode } = extractErrorBody(data);
    dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
    return {
      success: false,
      error: error || tr('apiErrors.receiptRequestFailed'),
      errorCode,
      holderErrors: data && typeof data === 'object' && data.holderErrors ? data.holderErrors : undefined,
      receiptRequest: data && typeof data === 'object' ? (data.receiptRequest ?? null) : null,
    };
  }
  return {
    success: true,
    receipts: Array.isArray(data.receipts) ? data.receipts : [],
    receiptRequest: data.receiptRequest ?? null,
    issued: Boolean(data.issued),
  };
};

/** Ricevute già emesse + stato della richiesta (per aggiornare la card). */
export const fetchReceipts = async (token: string): Promise<ReceiptsResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/receipt`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return await parseReceiptsResponse(response);
  } catch (error) {
    console.error('Fetch receipts error:', error);
    return { success: false, error: tr('apiErrors.connectionErrorShort') };
  }
};

/**
 * Richiede la ricevuta del soggiorno: holder = null → a nome della
 * prenotazione; altrimenti l'intestatario scelto (validato dal server).
 */
export const requestReceipt = async (
  token: string,
  holder: ReceiptHolderInput | null
): Promise<ReceiptsResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/receipt`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ holder }),
    });
    return await parseReceiptsResponse(response);
  } catch (error) {
    console.error('Request receipt error:', error);
    return { success: false, error: tr('apiErrors.connectionErrorShort') };
  }
};

/**
 * Sblocca i link di download delle ricevute: come per il codice di accesso,
 * serve l'email della prenotazione. errorCode 'email_mismatch' se non
 * corrisponde. La verifica vale per la sessione di pagina (stato locale).
 */
export const unlockReceiptDownloads = async (
  token: string,
  email: string
): Promise<ReceiptsResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/receipt/download`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('apiErrors.receiptUnlockFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      receipts: Array.isArray(data.receipts) ? data.receipts : [],
    };
  } catch (error) {
    console.error('Unlock receipt downloads error:', error);
    return { success: false, error: tr('apiErrors.connectionErrorShort') };
  }
};

// ============================================
// CLIMATE (controllo clima dall'app)
// ============================================

/**
 * Stato del clima per la prenotazione. Il server decide tutto: quali
 * apparecchi, se l'ospite è sul WiFi di casa e se il soggiorno è attivo.
 * apartments è vuoto quando non si può comandare.
 */
export const fetchClimate = async (token: string): Promise<ClimateResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/climate`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('apiErrors.climateStatusFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      available: Boolean(data.available),
      onHomeNetwork: Boolean(data.onHomeNetwork),
      stayActive: Boolean(data.stayActive),
      wifiName: typeof data.wifiName === 'string' ? data.wifiName : undefined,
      apartments: Array.isArray(data.apartments) ? data.apartments : [],
    };
  } catch (error) {
    console.error('Fetch climate error:', error);
    return {
      success: false,
      error: tr('apiErrors.connectionErrorShort'),
    };
  }
};

/**
 * Invia un comando a un apparecchio (campi assenti = restano come sono).
 * Il ponte di casa lo esegue entro circa un minuto: la risposta porta
 * l'apparecchio con pending:true e pendingCommand valorizzato.
 */
export const sendClimateCommand = async (
  token: string,
  id: string,
  command: ClimateCommand
): Promise<ClimateCommandResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/climate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, command }),
    });

    const data = await response.json();

    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('apiErrors.climateCommandFailed'),
        errorCode,
      };
    }

    return {
      success: true,
      device: data.device,
    };
  } catch (error) {
    console.error('Send climate command error:', error);
    return {
      success: false,
      error: tr('apiErrors.connectionErrorShort'),
    };
  }
};

// ============================================
// LAUNDRY (lavatrice: un lavaggio al giorno incluso, extra a pagamento)
// ============================================
// Il channel manager decide tutto: macchine della prenotazione, soggiorno
// in corso, crediti del giorno, prezzo. L'app mostra e preme "Accendi".

export type LaundryKind = 'lavatrice' | 'asciugatrice' | 'lavasciuga' | 'altro';
export type LaundryMachineState = 'off' | 'powered' | 'running' | 'finished' | 'occupied';

export interface LaundrySessionInfo {
  id: string;
  status: 'powered' | 'running' | 'finished' | 'expired' | 'interrupted';
  source: 'included' | 'paid' | 'staff' | 'unrequested';
  poweredAt: string;
  startedAt: string | null;
  endedAt: string | null;
  cycleKwh: number | null;
}

export interface LaundryMachine {
  id: string;
  kind: LaundryKind;
  name: string;
  apartmentId: string;
  apartmentName: string;
  common: boolean;
  online: boolean;
  powered: boolean;
  state: LaundryMachineState;
  session: LaundrySessionInfo | null;
  occupiedByOther: boolean;
  notifyRequested: boolean;
  includedPerDay: number;
  includedLeftToday: number;
  includedDayToday: boolean;
  paidLeft: number;
  unrequested: number;
  price: number;
  canStart: boolean;
  startBlockedReason: 'no_credit' | 'occupied' | 'offline' | 'stay_not_active' | null;
  lastCycleAt: string | null;
}

export interface LaundryResponse {
  success: boolean;
  available: boolean;
  stayActive: boolean;
  today?: string;
  includedResetsAt?: string;
  machines: LaundryMachine[];
  error?: string;
  errorCode?: string;
}

export interface LaundryStartResponse {
  success: boolean;
  alreadyOpen?: boolean;
  machine?: LaundryMachine | null;
  error?: string;
  errorCode?: string;
}

export interface LaundryCheckoutResponse {
  success: boolean;
  clientSecret?: string;
  publishableKey?: string;
  amount?: number;
  purchaseId?: string;
  error?: string;
  errorCode?: string;
}

const EMPTY_LAUNDRY: LaundryResponse = { success: false, available: false, stayActive: false, machines: [] };

export const fetchLaundry = async (token: string): Promise<LaundryResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/laundry`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) {
      return { ...EMPTY_LAUNDRY, error: data?.error || tr('apiErrors.laundryUnavailable'), errorCode: data?.errorCode };
    }
    return { ...EMPTY_LAUNDRY, ...data, machines: Array.isArray(data?.machines) ? data.machines : [] };
  } catch (error) {
    console.error('Error fetching laundry:', error);
    return { ...EMPTY_LAUNDRY, error: tr('apiErrors.networkError') };
  }
};

export interface LaundryChallengeResponse {
  success: boolean;
  code?: string;
  challenge?: string;
  expiresAt?: string;
  machine?: LaundryMachine | null;
  error?: string;
  errorCode?: string;
}

/** The 4-digit code the guest must type back to switch the socket on. */
export const requestLaundryChallenge = async (token: string, machineId: string): Promise<LaundryChallengeResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/laundry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'challenge', machineId }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data?.error || tr('apiErrors.notAvailable'), errorCode: data?.errorCode, machine: data?.machine ?? null };
    }
    return data;
  } catch (error) {
    console.error('Error requesting laundry challenge:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

/** "Notify me when it is free": one push when the socket switches off. */
export const requestLaundryNotify = async (token: string, machineId: string): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/laundry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'notify', machineId }),
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data?.error || tr('apiErrors.notAvailable'), errorCode: data?.errorCode };
    return { success: true };
  } catch (error) {
    console.error('Error requesting laundry notify:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

export const startLaundry = async (
  token: string,
  machineId: string,
  confirm: { code: string; challenge: string }
): Promise<LaundryStartResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/laundry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'start', machineId, code: confirm.code, challenge: confirm.challenge }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data?.error || tr('apiErrors.couldNotSwitchOn'), errorCode: data?.errorCode, machine: data?.machine ?? null };
    }
    return data;
  } catch (error) {
    console.error('Error starting laundry:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

export const createLaundryCheckout = async (
  token: string,
  kind: LaundryKind,
  quantity = 1
): Promise<LaundryCheckoutResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/laundry/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kind, quantity }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data?.error || tr('extras.sheet.error'), errorCode: data?.errorCode };
    }
    return data;
  } catch (error) {
    console.error('Error creating laundry checkout:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

// ============================================
// EXTRA & SERVIZI — chiamate
// ============================================

const EMPTY_EXTRAS: ExtrasCatalog = {
  success: false,
  items: [],
  purchasedExtras: [],
  pendingRequests: [],
  lateCheckout: null,
};

/**
 * Il catalogo di quello che QUESTA prenotazione può comprare adesso.
 * Le date e i prezzi arrivano dal server: l'app non calcola nulla, mostra.
 */
// ---------- Copia locale del catalogo ----------
//
// Ogni schermata degli Extra (elenco, dettaglio, gruppo, carrello) montava il
// catalogo da zero: rotellina e attesa del server a OGNI tocco. Ora l'ultimo
// catalogo buono resta in memoria (e nella sessione della scheda, per un
// ricaricamento): la pagina lo mostra subito e intanto chiede quello nuovo.
// Prezzi e acquisti restano quelli del server: la copia serve solo a non far
// aspettare, e viene sostituita appena arriva la risposta.

interface CatalogCacheEntry {
  at: number;
  data: ExtrasCatalog;
}

const CATALOG_STORE_PREFIX = 'nva_extras_catalog:';
const catalogMemory: Record<string, CatalogCacheEntry> = {};
// Richieste gia' in volo: MyStay, chat e pagina Extra che chiedono insieme
// fanno UNA sola chiamata.
const catalogInFlight: Record<string, Promise<ExtrasCatalog>> = {};

const catalogKey = (token: string, lang?: string): string => {
  const code = String(lang || '').toLowerCase().split('-')[0];
  // La coda del token (la firma) basta a distinguere le prenotazioni.
  return `${token.slice(-24)}|${code}`;
};

/** L'ultimo catalogo buono per questa prenotazione e lingua, se c'e'. */
export const peekBookingExtras = (token: string, lang?: string): CatalogCacheEntry | null => {
  if (!token) return null;
  const key = catalogKey(token, lang);
  const mem = catalogMemory[key];
  if (mem) return mem;
  try {
    const raw = sessionStorage.getItem(CATALOG_STORE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CatalogCacheEntry;
    if (!parsed || typeof parsed.at !== 'number' || !parsed.data || !Array.isArray(parsed.data.items)) return null;
    catalogMemory[key] = parsed;
    return parsed;
  } catch {
    return null;
  }
};

const rememberBookingExtras = (token: string, lang: string | undefined, data: ExtrasCatalog): void => {
  const key = catalogKey(token, lang);
  const entry: CatalogCacheEntry = { at: Date.now(), data };
  catalogMemory[key] = entry;
  try {
    sessionStorage.setItem(CATALOG_STORE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Spazio pieno o storage bloccato: resta la copia in memoria.
  }
};

export const fetchBookingExtras = (token: string, lang?: string): Promise<ExtrasCatalog> => {
  const key = catalogKey(token, lang);
  const pending = catalogInFlight[key];
  if (pending) return pending;
  const request = loadBookingExtras(token, lang).then((result) => {
    if (!result.error && result.success !== false) rememberBookingExtras(token, lang, result);
    return result;
  });
  catalogInFlight[key] = request;
  const clear = () => { delete catalogInFlight[key]; };
  request.then(clear, clear);
  return request;
};

const loadBookingExtras = async (token: string, lang?: string): Promise<ExtrasCatalog> => {
  try {
    // La lingua dell'app: il server traduce nomi e descrizioni in questa,
    // non in quella del check-in online, così cambiando lingua cambia
    // anche il catalogo.
    const code = String(lang || '').toLowerCase().split('-')[0];
    const query = code ? `?lang=${encodeURIComponent(code)}` : '';
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/extras${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return { ...EMPTY_EXTRAS, error: error || tr('apiErrors.extrasUnavailable'), errorCode };
    }
    return {
      ...EMPTY_EXTRAS,
      ...data,
      items: Array.isArray(data?.items) ? data.items : [],
      purchasedExtras: Array.isArray(data?.purchasedExtras) ? data.purchasedExtras : [],
      pendingRequests: Array.isArray(data?.pendingRequests) ? data.pendingRequests : [],
      lateCheckout: data?.lateCheckout ?? null,
      groupApartments: Array.isArray(data?.groupApartments) && data.groupApartments.length > 1
        ? data.groupApartments
        : undefined,
    };
  } catch (error) {
    console.error('Error fetching booking extras:', error);
    return { ...EMPTY_EXTRAS, error: tr('apiErrors.networkError') };
  }
};

/** I campi in più di un rifiuto del pagamento (magazzino, ordine minimo), solo se ci sono. */
function checkoutErrorExtras(data: any): Pick<ExtraCheckoutResponse, 'stockKind' | 'minOrder' | 'missing'> {
  const out: Pick<ExtraCheckoutResponse, 'stockKind' | 'minOrder' | 'missing'> = {};
  if (typeof data?.stockKind === 'string') out.stockKind = data.stockKind;
  if (typeof data?.minOrder === 'number') out.minOrder = data.minOrder;
  if (typeof data?.missing === 'number') out.missing = data.missing;
  return out;
}

/**
 * Apre il pagamento di un extra dentro l'app (Stripe embedded).
 * Il PREZZO non si manda: lo decide il server dal listino.
 */
export const createExtraCheckout = async (
  token: string,
  serviceId: string,
  quantity = 1,
  details?: ExtraCheckoutDetails,
  lang?: string,
  /** Gruppo: la prenotazione dell'appartamento scelto (assente = la propria). */
  targetBookingId?: string
): Promise<ExtraCheckoutResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/extras/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        serviceId, quantity, details: details || {}, lang,
        ...(targetBookingId ? { targetBookingId } : {}),
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('extras.sheet.error'),
        errorCode,
        availableUnits: typeof data?.availableUnits === 'number' ? data.availableUnits : undefined,
        ...checkoutErrorExtras(data),
      };
    }
    return data;
  } catch (error) {
    console.error('Error creating extra checkout:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

/**
 * IL CARRELLO: più servizi, una sessione Stripe sola. Il server ricontrolla
 * e riprezza ogni articolo; se uno non passa, risponde con il suo serviceId.
 */
export const createCartCheckout = async (
  token: string,
  items: Array<{ serviceId: string; quantity: number; details?: ExtraCheckoutDetails }>,
  lang?: string,
  /** Gruppo: la prenotazione dell'appartamento scelto (assente = la propria). */
  targetBookingId?: string
): Promise<ExtraCheckoutResponse & { serviceId?: string }> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/extras/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        items: items.map((i) => ({ serviceId: i.serviceId, quantity: i.quantity, details: i.details || {} })),
        lang,
        ...(targetBookingId ? { targetBookingId } : {}),
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return {
        success: false,
        error: error || tr('extras.sheet.error'),
        errorCode,
        serviceId: data?.serviceId,
        availableUnits: typeof data?.availableUnits === 'number' ? data.availableUnits : undefined,
        ...checkoutErrorExtras(data),
      };
    }
    return data;
  } catch (error) {
    console.error('Error creating cart checkout:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

/**
 * "Non si compra, si chiede": noleggi e culla oltre il termine d'ordine.
 * Nessun pagamento parte da qui — arriva una risposta da una persona.
 */
export const requestExtra = async (
  token: string,
  serviceId: string,
  note?: string,
  details?: ExtraCheckoutDetails
): Promise<ExtraRequestResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/extras/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // I dettagli servono al transfer: senza tratta, orario e volo il
      // gestore riceverebbe "un taxi" e dovrebbe riscrivere all'ospite.
      body: JSON.stringify({ serviceId, note: note || '', details: details || {} }),
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      dispatchBookingInvalidatedIfNeeded(response.status, errorCode);
      return { success: false, error: error || tr('apiErrors.requestUnavailable'), errorCode };
    }
    return data;
  } catch (error) {
    console.error('Error requesting extra:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

/**
 * "Cercato e non trovato" nel negozio Extra: la parola scritta dall'ospite
 * per cui non c'era niente. Al gestore dice cosa manca in listino.
 * Si manda e ci si dimentica: nessun errore a schermo e, senza una
 * sessione, nessuna chiamata. Il "una volta sola per ricerca" lo decide
 * chi chiama (che sa normalizzare la ricerca).
 */
export const reportExtrasSearchMiss = (token: string | null | undefined, query: string, locale: string): void => {
  const q = String(query || '').trim().slice(0, 200);
  if (!token || !q) return;
  try {
    fetch(`${CHANNEL_MANAGER_URL}/api/public/extras/search-miss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ query: q, locale }),
      // Niente `keepalive`: insieme all'header Authorization alcuni browser
      // rifiutano la richiesta, e la ricerca andrebbe persa in silenzio.
    }).catch(() => { /* fire-and-forget */ });
  } catch {
    /* fetch non disponibile: pazienza, non è un errore per l'ospite */
  }
};

// ============================================
// "Collega la tua prenotazione" (booking claim)
// ============================================

/** I portali che l'ospite può indicare. 'other' richiede `portalOther`. */
export type BookingClaimPortal = 'booking' | 'airbnb' | 'expedia' | 'other';

export interface BookingClaimPayload {
  portal: BookingClaimPortal;
  portalOther?: string;
  portalBookingRef?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  /** "YYYY-MM-DD" (giorno di Roma, come il check-in online). */
  checkIn: string;
  checkOut: string;
  numberOfGuests: number;
  locale?: string;
  deviceId?: string;
}

/**
 * L'esito della richiesta, deciso dal channel manager:
 * - 'checkin' = la finestra del check-in online è già aperta, niente richiesta
 * - 'pending' = richiesta registrata, il gestore deve approvarla
 * - 'linked'  = la prenotazione risulta già collegata a questa email
 */
export type BookingClaimMode = 'checkin' | 'pending' | 'linked';

export interface BookingClaimResponse {
  success: boolean;
  mode?: BookingClaimMode;
  requestId?: string;
  error?: string;
  errorCode?: string;
}

export type BookingClaimStatus = 'pending' | 'approved' | 'rejected';

export interface BookingClaimStatusResponse {
  success: boolean;
  status?: BookingClaimStatus;
  /** Motivo del rifiuto scritto dal gestore (non tradotto). */
  decisionNote?: string;
  /** Solo se approved: il riferimento da digitare al login. */
  loginReference?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Il rate limit della rotta pubblica risponde 429 (o 403) senza un
 * `errorCode` stabile: qui lo normalizziamo, così il modal può mostrare
 * "troppi tentativi" invece dell'errore generico.
 */
export const CLAIM_RATE_LIMITED_CODE = 'claim_rate_limited';

const claimErrorCodeFor = (status: number, errorCode: string | undefined): string | undefined => {
  if (errorCode) return errorCode;
  return status === 429 || status === 403 ? CLAIM_RATE_LIMITED_CODE : undefined;
};

/**
 * Chiede di collegare all'app una prenotazione fatta su un portale, quando
 * il check-in online non è ancora aperto e l'email della prenotazione è
 * quella finta del portale (quindi il login normale non può funzionare).
 */
export const submitBookingClaim = async (
  payload: BookingClaimPayload
): Promise<BookingClaimResponse> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      return {
        success: false,
        error: error || tr('claim.errors.generic'),
        errorCode: claimErrorCodeFor(response.status, errorCode),
      };
    }
    return data;
  } catch (error) {
    console.error('Error submitting booking claim:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

/**
 * Stato della richiesta salvata sul telefono: serve al banner del login
 * ("in verifica" / "collegata" / "non riuscita"). La rotta non restituisce
 * nessun dato della prenotazione, solo l'esito e il riferimento di accesso.
 */
export const getBookingClaimStatus = async (
  requestId: string,
  email: string
): Promise<BookingClaimStatusResponse> => {
  try {
    const query = `requestId=${encodeURIComponent(requestId)}&email=${encodeURIComponent(email)}`;
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/claim/status?${query}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json();
    if (!response.ok) {
      const { error, errorCode } = extractErrorBody(data);
      return {
        success: false,
        error: error || tr('claim.errors.generic'),
        errorCode: claimErrorCodeFor(response.status, errorCode),
      };
    }
    return data;
  } catch (error) {
    console.error('Error fetching booking claim status:', error);
    return { success: false, error: tr('apiErrors.networkError') };
  }
};

// ---- Gift card nell'app (lib/giftCardWallet) --------------------------------

export type GiftCardWalletResult = { code: string; card?: WalletCardView; error?: string };

/** POST /api/public/gift-cards/wallet: saldo, scadenza e utilizzi delle carte salvate. null = rete giù. */
export const fetchGiftCardWallet = async (
  cards: Array<{ code: string; key: string }>,
  locale: string,
): Promise<GiftCardWalletResult[] | null> => {
  if (cards.length === 0) return [];
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/gift-cards/wallet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cards, locale }),
    });
    const data = await response.json();
    if (!response.ok || !Array.isArray(data?.cards)) return null;
    return data.cards;
  } catch (error) {
    console.error('Error fetching gift card wallet:', error);
    return null;
  }
};

export type AddGiftCardResult =
  | { success: true; key: string; card: WalletCardView }
  | { success: false; errorCode: 'not_matching' | 'too_many_attempts' | 'invalid_input' | 'network' };

/** POST /api/public/gift-cards/wallet/add: "Aggiungi gift card" con codice + email. */
export const addGiftCardToWallet = async (code: string, email: string, locale: string): Promise<AddGiftCardResult> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/gift-cards/wallet/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, email, locale }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success && typeof data.key === 'string' && data.card) {
      return { success: true, key: data.key, card: data.card };
    }
    const known = ['not_matching', 'too_many_attempts', 'invalid_input'] as const;
    const errorCode = (known as readonly string[]).includes(data?.error) ? data.error : 'network';
    return { success: false, errorCode };
  } catch (error) {
    console.error('Error adding gift card:', error);
    return { success: false, errorCode: 'network' };
  }
};

/** GET /api/public/gift-cards/mine: le gift card dell'email della prenotazione collegata. null = errore. */
export const fetchMyGiftCards = async (
  token: string,
  locale: string,
): Promise<Array<{ code: string; key: string; card: WalletCardView }> | null> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/gift-cards/mine?locale=${encodeURIComponent(locale)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok || !Array.isArray(data?.cards)) return null;
    return data.cards;
  } catch (error) {
    console.error('Error fetching my gift cards:', error);
    return null;
  }
};

// ---- VIP Ticket nel Wallet (lib/vipTicket) ----------------------------------

export type VipWalletPassResult = { ok: true; url: string } | { ok: false; fallback: boolean };

/** POST /api/public/booking/vip-pass: dove andare per aggiungere il pass personalizzato. */
export const requestVipWalletPass = async (
  token: string,
  platform: 'apple' | 'google' | 'other',
  lang: string,
): Promise<VipWalletPassResult> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/booking/vip-pass`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ platform, lang }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success && typeof data.url === 'string' && /^https:\/\//.test(data.url)) {
      return { ok: true, url: data.url };
    }
    // Solo "soggiorno finito" dice di non scaricare niente; il resto ripiega sul pass di sempre.
    return { ok: false, fallback: data?.fallback !== false };
  } catch (error) {
    console.error('Error requesting VIP wallet pass:', error);
    return { ok: false, fallback: true };
  }
};

// ---- Classifica dei giochi (pages/services/TaralloGamePage) ------------------

export interface GameLeaderboardRow {
  rank: number;
  firstName: string;
  /** L'iniziale del cognome (channel manager aggiornato): "Giulia B.". */
  initial?: string;
  /** Il mese del soggiorno, "2026-10" (channel manager aggiornato). */
  stayMonth?: string;
  score: number;
  maxTile: number;
  me: boolean;
}

export interface GameLeaderboard {
  weekKey: string;
  top: GameLeaderboardRow[];
  me: { rank: number; score: number; maxTile: number } | null;
  /** La classifica di sempre; senza (channel manager vecchio) c'è solo la settimana. */
  allTime?: { top: GameLeaderboardRow[]; me: GameLeaderboard['me'] };
}

const parseLeaderboard = (data: unknown): GameLeaderboard | null => {
  const d = data as { success?: boolean; weekKey?: unknown; top?: unknown; me?: unknown; allTime?: { top?: unknown; me?: unknown } } | null;
  if (!d?.success || !Array.isArray(d.top)) return null;
  const board: GameLeaderboard = { weekKey: String(d.weekKey || ''), top: d.top as GameLeaderboardRow[], me: (d.me as GameLeaderboard['me']) || null };
  if (d.allTime && Array.isArray(d.allTime.top)) {
    board.allTime = { top: d.allTime.top as GameLeaderboardRow[], me: (d.allTime.me as GameLeaderboard['me']) || null };
  }
  return board;
};

/** GET /api/public/games/leaderboard: i primi 10 della settimana (e di sempre) e la mia posizione. null = non disponibile. */
export const fetchGameLeaderboard = async (token: string, game: string): Promise<GameLeaderboard | null> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/games/leaderboard?game=${encodeURIComponent(game)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return parseLeaderboard(await response.json().catch(() => null));
  } catch (error) {
    console.error('Error fetching game leaderboard:', error);
    return null;
  }
};

/**
 * POST /api/public/games/score: il punteggio (a fine partita o mentre si
 * gioca); torna le classifiche aggiornate. `keepalive`: parte anche se
 * l'app si sta chiudendo.
 */
export const submitGameScore = async (
  token: string,
  input: { game: string; score: number; maxTile: number; moves: number },
  options: { keepalive?: boolean } = {},
): Promise<GameLeaderboard | null> => {
  try {
    const response = await fetch(`${CHANNEL_MANAGER_URL}/api/public/games/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(input),
      ...(options.keepalive ? { keepalive: true } : {}),
    });
    return parseLeaderboard(await response.json().catch(() => null));
  } catch (error) {
    console.error('Error submitting game score:', error);
    return null;
  }
};
